import { Link } from 'react-router-dom';
import { useMemo } from 'react';

import { Button, EditIcon } from '@occc/ui-design-system';
import { Page, useAppLayoutContextConsumer } from '@occc-app/design-system';
import { Feature, useIsUserPermittedTo } from '@occc/utils-access-control';
import { useGetSubBrandByUuid } from '@occc/utils';

import { TriggeredMessageState } from './triggered-message-state';

import { useTriggeredMessage } from '../../hooks/use-triggered-message.hook';
import { routeCreator, getViewLinks } from '../../triggered-messages.utils';
import { EDIT_BUTTON_TITLE, PAGE_TITLE } from '../triggered-message-view.constants';
import { StatusBadge } from '../../components/status-badge';

type TriggeredMessageDetailsProps = {
  messageId: string;
  scope?: string;
};

export const TriggeredMessageDetails = (props: TriggeredMessageDetailsProps) => {
  const { messageId, scope } = props;

  const { data: triggeredMessage, loading } = useTriggeredMessage(messageId);
  const subBrand = useGetSubBrandByUuid(scope);

  useAppLayoutContextConsumer(
    useMemo(() => getViewLinks(triggeredMessage, subBrand).links, [subBrand, triggeredMessage]),
  );

  const isUserPermittedToEdit = useIsUserPermittedTo(Feature.TRIGGERED_MESSAGES_EDIT);
  const editTo = scope
    ? routeCreator.editScoped.to(scope, messageId)
    : routeCreator.edit.to(messageId);

  return (
    <Page
      browserTabTitle={PAGE_TITLE}
      pageHeaderProps={{
        title: triggeredMessage?.description || PAGE_TITLE,
        status: triggeredMessage ? <StatusBadge status={triggeredMessage.status} /> : undefined,
      }}
      actions={
        isUserPermittedToEdit ? (
          <Button
            startIcon={<EditIcon />}
            color='secondary'
            variant='contained'
            component={Link}
            to={editTo}
            role='button'
          >
            {EDIT_BUTTON_TITLE}
          </Button>
        ) : null
      }
      containerProps={{ variant: 'md' }}
    >
      <TriggeredMessageState message={triggeredMessage} loading={loading} />
    </Page>
  );
};
